import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Upload, FileUp, CheckCircle, AlertTriangle, X } from 'lucide-react'
import Papa from 'papaparse'
import { formatDate, formatNumber } from '@/lib/utils'

const EXPECTED_FIELDS = [
  'fecha',
  'hora',
  'ticket_numero',
  'local',
  'caja',
  'cliente',
  'empleado',
  'cantidad',
  'articulo',
  'subarticulo',
  'base',
  'impuestos',
  'dto_total',
  'neto',
] as const

const FIELD_LABELS: Record<string, string> = {
  fecha: 'Fecha',
  hora: 'Hora',
  ticket_numero: 'Nº Ticket',
  local: 'Establecimiento',
  caja: 'Caja',
  cliente: 'Cliente',
  empleado: 'Empleado',
  cantidad: 'Uds. Vendidas',
  articulo: 'Artículo',
  subarticulo: 'Subartículo',
  base: 'Base',
  impuestos: 'Impuestos',
  dto_total: 'Dto Total',
  neto: 'Neto',
}

const NUMERIC_FIELDS = ['cantidad', 'base', 'impuestos', 'dto_total', 'neto']

type ExpectedField = (typeof EXPECTED_FIELDS)[number]

interface ColumnMapping {
  [csvColumn: string]: ExpectedField | ''
}

interface ImportHistoryEntry {
  // Fecha real de importación (YYYY-MM-DD del created_at)
  fecha_import: string
  row_count: number
  // Rango de fechas de los tickets que abarca esa importación
  ticket_min?: string
  ticket_max?: string
}

interface SavedMappingConfig {
  id: number
  mapping: ColumnMapping
}

type ImportStep = 'select' | 'preview' | 'mapping' | 'importing' | 'done'

interface LocalRow {
  id: number
  nombre: string
}

export default function CargaVentas() {
  const { localesEditables, isSuperadmin } = useAuth()
  const [step, setStep] = useState<ImportStep>('select')
  const [file, setFile] = useState<File | null>(null)
  const [parsedData, setParsedData] = useState<Record<string, string>[]>([])
  const [csvColumns, setCsvColumns] = useState<string[]>([])
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({})
  const [importing, setImporting] = useState(false)
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 })
  const [importResult, setImportResult] = useState<{ success: number; errors: number } | null>(null)
  const [errorRows, setErrorRows] = useState<number[]>([])
  const [importHistory, setImportHistory] = useState<ImportHistoryEntry[]>([])
  const [savedMapping, setSavedMapping] = useState<SavedMappingConfig | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  // Local destino: imprescindible. Todas las filas se asignan a este local
  // independientemente de lo que diga la columna "Establecimiento" del CSV.
  const [locales, setLocales] = useState<LocalRow[]>([])
  const [selectedLocalId, setSelectedLocalId] = useState<number | null>(null)

  // Load saved mapping config, import history y locales on mount
  useEffect(() => {
    loadSavedMapping()
    loadImportHistory()
    loadLocales()
  }, [])

  async function loadLocales() {
    // Leemos de locales_compra_v2 que es donde la pantalla "Locales" del
    // sidebar crea/edita. Está sincronizada con tb_v2.locales vía trigger,
    // así que el id que guardamos sirve también para BI y ventas_staging.
    const { data } = await supabase
      .from('locales_compra_v2')
      .select('id, nombre')
      .eq('activo', true)
      .order('nombre')
    if (data) {
      // Cargar ventas requiere ESCRITURA en CargaVentas — filtrar.
      const editIds = localesEditables('CargaVentas')
      const todos = isSuperadmin || (editIds.length === 1 && editIds[0] === -1)
      const filtered = todos ? data : (data as LocalRow[]).filter(l => editIds.includes(l.id))
      setLocales(filtered)
      if (filtered.length === 1) setSelectedLocalId((filtered[0] as LocalRow).id)
    }
  }

  async function loadSavedMapping() {
    const { data } = await supabase
      .from('config_importaciones_v2')
      .select('*')
      .eq('tipo', 'ventas')
      .order('created_at', { ascending: false })
      .limit(1)
      .single()

    if (data?.mapping) {
      setSavedMapping({ id: data.id, mapping: data.mapping })
    }
  }

  async function loadImportHistory() {
    // Trae las últimas 5000 filas ordenadas por created_at desc.
    // Agrupa por la FECHA DE IMPORTACIÓN (date(created_at)), no por la
    // fecha del ticket — un CSV de 2023 importado hoy debe aparecer con
    // fecha de hoy.
    const { data } = await supabase
      .from('ventas_raw_v2')
      .select('fecha, created_at')
      .order('created_at', { ascending: false })
      .limit(5000)

    if (data && data.length > 0) {
      type Bucket = { row_count: number; ticket_min: string; ticket_max: string }
      const grouped = data.reduce<Record<string, Bucket>>((acc, row) => {
        const ca = (row as { created_at?: string }).created_at
        const key = ca ? ca.slice(0, 10) : 'sin_fecha'
        const ticketF = (row.fecha as string | null) ?? ''
        if (!acc[key]) acc[key] = { row_count: 0, ticket_min: ticketF || '9999-12-31', ticket_max: ticketF || '0000-01-01' }
        acc[key].row_count += 1
        if (ticketF) {
          if (ticketF < acc[key].ticket_min) acc[key].ticket_min = ticketF
          if (ticketF > acc[key].ticket_max) acc[key].ticket_max = ticketF
        }
        return acc
      }, {})

      const history: ImportHistoryEntry[] = Object.entries(grouped)
        .map(([fecha_import, b]) => ({
          fecha_import,
          row_count: b.row_count,
          ticket_min: b.ticket_min === '9999-12-31' ? undefined : b.ticket_min,
          ticket_max: b.ticket_max === '0000-01-01' ? undefined : b.ticket_max,
        }))
        .sort((a, b) => b.fecha_import.localeCompare(a.fecha_import))
        .slice(0, 5)

      setImportHistory(history)
    } else {
      setImportHistory([])
    }
  }

  // --- File handling ---
  const handleFile = useCallback((selectedFile: File) => {
    if (!selectedFile.name.endsWith('.csv')) {
      setParseError('Solo se aceptan archivos .csv')
      return
    }
    setParseError(null)
    setFile(selectedFile)

    Papa.parse<Record<string, string>>(selectedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0 && results.data.length === 0) {
          setParseError(`Error al parsear el archivo: ${results.errors[0].message}`)
          return
        }
        const columns = results.meta.fields || []
        setCsvColumns(columns)
        setParsedData(results.data)

        // Auto-map columns by name similarity
        const mapping: ColumnMapping = {}
        const autoMap: Record<string, ExpectedField> = {
          'fecha': 'fecha',
          'hora': 'hora',
          'serie / número': 'ticket_numero',
          'serie/número': 'ticket_numero',
          'serie / numero': 'ticket_numero',
          'establecimiento': 'local',
          'caja': 'caja',
          'cliente': 'cliente',
          'empleado': 'empleado',
          'uds.v': 'cantidad',
          'uds v': 'cantidad',
          'unidades': 'cantidad',
          'artículo': 'articulo',
          'articulo': 'articulo',
          'subartículo': 'subarticulo',
          'subarticulo': 'subarticulo',
          'base': 'base',
          'impuestos': 'impuestos',
          'dto total': 'dto_total',
          'dto_total': 'dto_total',
          'neto': 'neto',
        }

        columns.forEach((col) => {
          const normalized = col.toLowerCase().trim()
          mapping[col] = autoMap[normalized] || ''
        })

        // Apply saved mapping if available
        if (savedMapping?.mapping) {
          const saved = savedMapping.mapping
          columns.forEach((col) => {
            if (saved[col]) {
              mapping[col] = saved[col]
            }
          })
        }

        setColumnMapping(mapping)
        setStep('preview')
      },
      error: (error) => {
        setParseError(`Error al leer el archivo: ${error.message}`)
      },
    })
  }, [savedMapping])

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setDragOver(false)
      const droppedFile = e.dataTransfer.files[0]
      if (droppedFile) handleFile(droppedFile)
    },
    [handleFile]
  )

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const onDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  const onFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0]
      if (selectedFile) handleFile(selectedFile)
    },
    [handleFile]
  )

  // --- Column mapping ---
  function updateMapping(csvCol: string, targetField: ExpectedField | '') {
    setColumnMapping((prev) => ({ ...prev, [csvCol]: targetField }))
  }

  function getMappedFields(): ExpectedField[] {
    return Object.values(columnMapping).filter((v): v is ExpectedField => v !== '')
  }

  function getUnmappedRequired(): ExpectedField[] {
    const mapped = getMappedFields()
    const required: ExpectedField[] = ['fecha', 'articulo', 'cantidad']
    return required.filter((f) => !mapped.includes(f))
  }

  // --- Validation ---
  function validateRows(): { valid: Record<string, string>[]; errorIndices: number[] } {
    const requiredFields: ExpectedField[] = ['fecha', 'articulo', 'cantidad']
    const reverseMap: Record<string, string> = {}
    Object.entries(columnMapping).forEach(([csvCol, field]) => {
      if (field) reverseMap[field] = csvCol
    })

    const valid: Record<string, string>[] = []
    const errorIndices: number[] = []

    parsedData.forEach((row, idx) => {
      const hasRequired = requiredFields.every((f) => {
        const csvCol = reverseMap[f]
        return csvCol && row[csvCol]?.toString().trim()
      })
      if (hasRequired) {
        valid.push(row)
      } else {
        errorIndices.push(idx + 1)
      }
    })

    return { valid, errorIndices }
  }

  // --- Import ---
  async function handleImport() {
    setStep('importing')
    setImporting(true)
    setImportResult(null)

    const reverseMap: Record<string, string> = {}
    Object.entries(columnMapping).forEach(([csvCol, field]) => {
      if (field) reverseMap[field] = csvCol
    })

    const { valid, errorIndices } = validateRows()
    setErrorRows(errorIndices)

    // Resolver el nombre del local seleccionado (todas las filas llevan
    // este local, sobrescribiendo lo que venga del CSV)
    const localElegido = locales.find(l => l.id === selectedLocalId)
    const localNombre = localElegido?.nombre ?? null

    const mappedRows = valid.map((row) => {
      const mapped: Record<string, string | number | null> = {}
      EXPECTED_FIELDS.forEach((field) => {
        const csvCol = reverseMap[field]
        if (csvCol) {
          const val = row[csvCol]?.toString().trim() ?? ''
          if (NUMERIC_FIELDS.includes(field)) {
            const num = parseFloat(val.replace(',', '.'))
            mapped[field] = isNaN(num) ? null : num
          } else {
            mapped[field] = val || null
          }
        } else {
          mapped[field] = null
        }
      })
      // Forzar el local destino seleccionado por el usuario
      if (localNombre) mapped['local'] = localNombre
      return mapped
    })

    const total = mappedRows.length
    setImportProgress({ done: 0, total })

    const BATCH_SIZE = 200
    let successCount = 0
    let errorCount = errorIndices.length

    for (let i = 0; i < mappedRows.length; i += BATCH_SIZE) {
      const batch = mappedRows.slice(i, i + BATCH_SIZE)
      const { error } = await supabase.from('ventas_raw_v2').insert(batch)

      if (error) {
        errorCount += batch.length
      } else {
        successCount += batch.length
      }
      setImportProgress({ done: Math.min(i + BATCH_SIZE, total), total })
    }

    // Save mapping config for future use
    const mappingToSave = { ...columnMapping }
    if (savedMapping?.id) {
      await supabase
        .from('config_importaciones_v2')
        .update({ mapping: mappingToSave, updated_at: new Date().toISOString() })
        .eq('id', savedMapping.id)
    } else {
      await supabase
        .from('config_importaciones_v2')
        .insert({ tipo: 'ventas', mapping: mappingToSave })
    }

    setImportResult({ success: successCount, errors: errorCount })
    setImporting(false)
    setStep('done')
    loadImportHistory()
  }

  // --- Reset ---
  function resetAll() {
    setStep('select')
    setFile(null)
    setParsedData([])
    setCsvColumns([])
    setColumnMapping({})
    setImportResult(null)
    setErrorRows([])
    setParseError(null)
    setImportProgress({ done: 0, total: 0 })
  }

  // --- Render ---
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Upload className="h-7 w-7 text-blue-500" />
        <h1 className="text-2xl font-bold">Carga de Ventas</h1>
      </div>

      {/* Step 1: File selector */}
      {step === 'select' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Seleccionar archivo CSV</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Selector de local destino (obligatorio) */}
            <div>
              <label className="block text-sm font-medium mb-1.5">
                Local destino <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedLocalId ?? ''}
                onChange={(e) => setSelectedLocalId(e.target.value ? Number(e.target.value) : null)}
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Elegir local —</option>
                {locales.map((l) => (
                  <option key={l.id} value={l.id}>{l.nombre}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Todas las filas del CSV se asignarán a este local
                (sobrescribiendo la columna "Establecimiento" si existe).
              </p>
              {locales.length === 0 && (
                <p className="mt-1 text-xs text-amber-600">
                  No hay locales dados de alta. Crea uno antes desde "Locales".
                </p>
              )}
            </div>

            {/* Zona de drop / selector de archivo */}
            <div
              onDrop={selectedLocalId ? onDrop : undefined}
              onDragOver={selectedLocalId ? onDragOver : undefined}
              onDragLeave={selectedLocalId ? onDragLeave : undefined}
              className={`
                flex flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed p-12
                transition-colors duration-200
                ${!selectedLocalId
                  ? 'cursor-not-allowed border-gray-200 bg-gray-100/50 opacity-60 dark:border-gray-700 dark:bg-gray-800/20'
                  : dragOver
                    ? 'cursor-pointer border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                    : 'cursor-pointer border-gray-300 bg-gray-50/50 hover:border-blue-400 hover:bg-blue-50/50 dark:border-gray-600 dark:bg-gray-800/30 dark:hover:border-blue-500 dark:hover:bg-blue-950/10'
                }
              `}
              onClick={() => selectedLocalId && document.getElementById('csv-file-input')?.click()}
            >
              <FileUp className={`h-12 w-12 ${!selectedLocalId ? 'text-gray-300' : dragOver ? 'text-blue-500' : 'text-gray-400'}`} />
              <div className="text-center">
                <p className="text-base font-medium text-gray-700 dark:text-gray-300">
                  {selectedLocalId
                    ? 'Arrastrá tu archivo CSV aquí'
                    : 'Primero elegí un local'}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {selectedLocalId ? 'o hacé click para seleccionar' : ''}
                </p>
              </div>
              <Input
                id="csv-file-input"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={onFileInput}
                disabled={!selectedLocalId}
              />
            </div>
            {parseError && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-400">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                {parseError}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Preview */}
      {step === 'preview' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Vista previa — {file?.name}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={resetAll}>
                <X className="mr-1 h-4 w-4" /> Cancelar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Total de filas: <span className="font-semibold">{formatNumber(parsedData.length, 0)}</span>
            </p>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 dark:bg-gray-800">
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    {csvColumns.map((col) => (
                      <th key={col} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsedData.slice(0, 10).map((row, idx) => (
                    <tr key={idx} className="border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                      {csvColumns.map((col) => (
                        <td key={col} className="px-3 py-2 whitespace-nowrap">
                          {row[col] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {parsedData.length > 10 && (
              <p className="text-xs text-gray-400">
                Mostrando 10 de {formatNumber(parsedData.length, 0)} filas
              </p>
            )}
            <div className="flex justify-end">
              <Button onClick={() => setStep('mapping')}>
                Continuar a mapeo de columnas
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Column mapping */}
      {step === 'mapping' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Mapeo de columnas</CardTitle>
              <Button variant="ghost" size="sm" onClick={resetAll}>
                <X className="mr-1 h-4 w-4" /> Cancelar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Asigná cada columna del CSV al campo correspondiente de ventas.
            </p>
            <div className="space-y-3">
              {csvColumns.map((col) => (
                <div key={col} className="flex items-center gap-4">
                  <span className="w-48 truncate text-sm font-medium" title={col}>
                    {col}
                  </span>
                  <span className="text-gray-400">→</span>
                  <select
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
                    value={columnMapping[col] || ''}
                    onChange={(e) => updateMapping(col, e.target.value as ExpectedField | '')}
                  >
                    <option value="">— No importar —</option>
                    {EXPECTED_FIELDS.map((field) => {
                      const alreadyMapped = Object.entries(columnMapping).some(
                        ([k, v]) => v === field && k !== col
                      )
                      return (
                        <option key={field} value={field} disabled={alreadyMapped}>
                          {FIELD_LABELS[field] || field}
                          {alreadyMapped ? ' (ya asignado)' : ''}
                        </option>
                      )
                    })}
                  </select>
                  {columnMapping[col] && (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                </div>
              ))}
            </div>

            {getUnmappedRequired().length > 0 && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <p className="font-medium">Campos obligatorios sin asignar:</p>
                  <p>{getUnmappedRequired().map((f) => FIELD_LABELS[f] || f).join(', ')}</p>
                </div>
              </div>
            )}

            {/* Validation preview */}
            {(() => {
              const { errorIndices } = validateRows()
              return errorIndices.length > 0 ? (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-400">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <p>
                    <span className="font-medium">{errorIndices.length} filas con errores</span>{' '}
                    (campos requeridos vacíos: fecha, artículo, cantidad). Estas filas no se importarán.
                  </p>
                </div>
              ) : null
            })()}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep('preview')}>
                Volver
              </Button>
              <Button onClick={handleImport} disabled={getMappedFields().length === 0}>
                Importar {formatNumber(parsedData.length, 0)} filas
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Importing / Progress */}
      {step === 'importing' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Importando datos...</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                <span>Progreso</span>
                <span>{formatNumber(importProgress.done, 0)} / {formatNumber(importProgress.total, 0)}</span>
              </div>
              <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-full rounded-full bg-blue-500 transition-all duration-300"
                  style={{
                    width: importProgress.total > 0
                      ? `${(importProgress.done / importProgress.total) * 100}%`
                      : '0%',
                  }}
                />
              </div>
            </div>
            <p className="text-center text-sm text-gray-500">
              No cierres esta ventana mientras se importan los datos...
            </p>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Done */}
      {step === 'done' && importResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Importación completada
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg bg-green-50 p-4 text-center dark:bg-green-950/30">
                <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                  {formatNumber(importResult.success, 0)}
                </p>
                <p className="text-sm text-green-600 dark:text-green-500">Filas importadas</p>
              </div>
              <div className="rounded-lg bg-red-50 p-4 text-center dark:bg-red-950/30">
                <p className="text-2xl font-bold text-red-700 dark:text-red-400">
                  {formatNumber(importResult.errors, 0)}
                </p>
                <p className="text-sm text-red-600 dark:text-red-500">Filas con errores</p>
              </div>
            </div>

            {errorRows.length > 0 && (
              <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-400">
                <p className="font-medium">Filas omitidas (campos requeridos vacíos):</p>
                <p className="mt-1">
                  Filas: {errorRows.slice(0, 20).join(', ')}
                  {errorRows.length > 20 && ` y ${errorRows.length - 20} más`}
                </p>
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={resetAll}>
                <Upload className="mr-2 h-4 w-4" />
                Nueva importación
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import history */}
      {importHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Importaciones recientes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 dark:bg-gray-800">
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha de carga</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Periodo del CSV</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Filas</th>
                  </tr>
                </thead>
                <tbody>
                  {importHistory.map((entry) => (
                    <tr key={entry.fecha_import} className="border-b last:border-b-0">
                      <td className="px-4 py-2">{formatDate(entry.fecha_import)}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {entry.ticket_min && entry.ticket_max
                          ? entry.ticket_min === entry.ticket_max
                            ? formatDate(entry.ticket_min)
                            : `${formatDate(entry.ticket_min)} → ${formatDate(entry.ticket_max)}`
                          : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">{formatNumber(entry.row_count, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
